import cn from 'classnames';
import { useState } from 'react';
import { ButtonUI } from 'sonata-react-components';

import { MainItem } from './MainItem';
import './sidebar.styles.scss';

import { isChildGroup, type SidebarChildGroup, type SidebarChildItemConfig, type SidebarConfig } from './sidebar.types';

function filterChildren(
  children: (SidebarChildItemConfig | SidebarChildGroup)[] | undefined
): (SidebarChildItemConfig | SidebarChildGroup)[] | undefined {
  if (!children) {
    return undefined;
  }

  return children
    .map(child => (isChildGroup(child) ? { ...child, items: child.items.filter(item => item.show !== false) } : child))
    .filter(child => (isChildGroup(child) ? child.items.length > 0 : child.show !== false));
}

type Props<T> = {
  active: T | null;
  className: string;
  config: SidebarConfig<T>;
};

export function Sidebar<T extends string>({ active, className, config }: Readonly<Props<T>>) {
  if (!config.length) {
    throw new Error(`${Sidebar.name} needs at least one item to be showed`);
  }
  const [status, setStatus] = useState('open');

  const handleButtonClick = () => {
    setStatus(prevState => (prevState === 'open' ? 'closed' : 'open'));
  };

  const sideBarClass = cn('sideBar', className, {
    closed: status === 'closed'
  });

  const collapseButtonIconClass = cn('collapseButtonContainer', {
    'collapseButtonContainer--closed': status === 'closed'
  });

  return (
    <div className={sideBarClass}>
      <div className={collapseButtonIconClass}>
        <ButtonUI
          icon={status === 'open' ? 'fa-solid fa-chevrons-left' : 'fa-solid fa-chevrons-right'}
          id={'collapseSidebar'}
          onClick={handleButtonClick}
          shape="squareRounded"
          size="ultraSmall"
          type="tertiary"
        />
      </div>
      {config.map(item => (
        <MainItem<T> isCollapsed={status === 'closed'} isSelected={item.id === active} key={item.id} {...item}>
          {filterChildren(item.children)}
        </MainItem>
      ))}
    </div>
  );
}
